import { addLoader, flashElement, getRedirectUrl, hideElem, showElem, typewriterEffect } from '../../shared/shared';

import {
    roundPrice as round,
    formatPrice as format,
} from '../lib/price-format';
import quoteFormHndb from '../templates/quote/quote-form.handlebars';

import Handlebars, { getTranslationByPattern } from '../../shared/handlebar-helpers';
import EventBus from '../../shared/bus';
import ProspectForm from './prospect-form';
import Notification from '../../shared/notification';

import { LocalQuote } from '../local-quote';
import Activity from './activity';
import LoadCustomerCart from './load-customer-cart';
import RichTextEditor from '../../shared/rich-text-editor';
import { Modal } from '../../shared/modal';
import { View } from './view';
import Toast from '../../shared/toast';
import { prepareNewCart } from '../menu-handlers/new-cart-click';

class Quote extends View {
    constructor(view, data, app) {
        super(view);
        this.app = app;
        this.quoteEl = document.createElement('div');
        this.data = data;
        this.events = new EventBus();
    }

    roundPrice = (a) => round(a, this.data.quote.currency.decimal_place);
    formatPrice = (a) => format(a, this.data.quote.currency);
    notif = new Notification({ type: 'loading' });
    
    #onCloseRedirect = null;
    #focusSku = null;
    #messageEditor = null;

    edited = false;
    saving = {
        interval: null,
        retries: 0
    };

    #updateProductName = async(titleEl, cartId, cartLineId, quotePrice, qty) => {
        titleEl.disabled = true;
        titleEl.value = `${Handlebars.translation.general.updating}...`;

        const { data } = await this.app.service.getQuoteProductName(
            cartId,
            cartLineId,
            quotePrice,
            qty
        );

        if (data.quote_product_name) {
            titleEl.value = data.quote_product_name;
            flashElement(titleEl);
        } else {
            titleEl.value = '';
            titleEl.textContent = '';
        }
        titleEl.disabled = false;
    }

    #listeners = () => {
        this.quoteEl.addEventListener('change', async (e) => {
            if (e.target.id === 'quote-line-discount') {
                const cartItemEl = e.target.closest('[data-cart-line]');
                const lineItem = this.#getLineItem(
                    cartItemEl.dataset.quoteLineId
                );
                const discount = this.#getDiscount(e.target.value);
                let priceUpdated = false;

                switch (discount.kind) {
                    case 'invalid':
                        e.target.value = '';
                        lineItem.quote_price = lineItem.tempPrice;
                        priceUpdated = true;
                        break;
                    case 'subtract':
                        if (discount.value <= lineItem.price) {
                            lineItem.quote_price =
                                lineItem.price - discount.value;
                            lineItem.tempPrice = lineItem.price;
                            priceUpdated = true;
                        } else {
                            lineItem.quote_price = lineItem.tempPrice;
                            e.target.value = '';
                            priceUpdated = true;
                        }
                        break;
                    case 'percentual':
                        if (discount.value <= 100 && discount.value >= 0) {
                            lineItem.quote_price =
                                lineItem.price -
                                lineItem.price * (discount.value / 100);
                            lineItem.tempPrice = lineItem.price;
                            priceUpdated = true;
                        }
                        break;
                    case 'flat':
                        /* Avoid having negative lineDiscounts on totals.
                        ** If the flat price is higher than the product price, we trick the totals into 
                        ** thinking the line item is worth the discount value.
                        */
                        if (discount.value > lineItem.price) {
                            lineItem.tempPrice = discount.value;
                        } else {
                            lineItem.tempPrice = lineItem.price;
                        }

                        lineItem.quote_price = discount.value;
                        priceUpdated = true;
                        break;
                }
    
                if (priceUpdated) {
                    // @todo: use a suscriber to perform this update
                    cartItemEl.querySelector(
                        '#quote-line-quote'
                    ).textContent =
                        `${this.formatPrice(lineItem.quote_price)}`;
                    cartItemEl.querySelector(
                        '#quote-line-total'
                    ).textContent =
                        `${this.formatPrice(lineItem.quote_price * lineItem.quantity)}`;
                    flashElement(
                        cartItemEl.querySelector('#quote-line-quote')
                    );
                    flashElement(
                        cartItemEl.querySelector('#quote-line-total')
                    );
                    this.events.emit('update:cartline', {
                        lineItem: cartItemEl,
                    });
                    const quoteLineTitleEl =
                        cartItemEl.querySelector('#quote-line-title');

                    await this.#updateProductName(quoteLineTitleEl, this.data.quote.seller_cart_id, cartItemEl.dataset.quoteLineId, lineItem.quote_price, lineItem.quantity);
                    this.edited = true;
                }
            }

            if (e.target.id === 'quote-global-discount-value') {
                this.data.quote.discount = e.target.value;
                return this.events.emit('update:cartline', {
                    discount: e.target.value,
                });
            }

            if (e.target.id === 'quote-line-qty') {
                const cartItemEl = e.target.closest('[data-cart-line]');
                const lineItem = this.#getLineItem(
                    cartItemEl.dataset.quoteLineId
                );
                const qty = e.target.value;
                lineItem.quantity = qty;

                cartItemEl.querySelector(
                    '#quote-line-total'
                ).textContent =
                    `${this.formatPrice(lineItem.quote_price * lineItem.quantity)}`;

                
                // @todo: use a suscriber to perform this update
                flashElement(
                    cartItemEl.querySelector('#quote-line-total')
                );

                this.events.emit('update:cartline', {
                    discount: e.target.value,
                    origin: 'line-qty',
                });

                const quoteLineTitleEl = cartItemEl.querySelector('#quote-line-title');

                await this.#updateProductName(quoteLineTitleEl, this.data.quote.seller_cart_id, cartItemEl.dataset.quoteLineId, lineItem.quote_price, lineItem.quantity);

                return this.edited = true;
            }

            if (e.target.id === 'quote-win') {
                if (this.data.quote.prospect?.id) {
                    this.#enableShareOptions();
                    this.#enableAIInstructions();
                }
            }
        });

        /* Timeout id for debouncing quote prospect input*/
        let quoteProspectTimeoutId, listening;
        const resultsContainerEl = this.quoteEl.querySelector(
            '#os-prospect-search-dropdown'
        );

        const handleOutsideDropdownClick = (e) => {
            if (!e.target.closest('.os-input-with-dropdown')) {
                resultsContainerEl.innerHTML = '';
                hideElem(resultsContainerEl);
                listening = false;
                document.body.removeEventListener(
                    'click',
                    handleOutsideDropdownClick
                );
            }
        };

        this.quoteEl.addEventListener('input', (e) => {

            if (e.target.id === 'quote-prospect') {
                // Perform debounce on the Quote Prospect input
                clearTimeout(quoteProspectTimeoutId);

                if (e.target.value.length < 4 && !resultsContainerEl.hasChildNodes()) {
                    const helpMessage = document.createElement('p');
                    helpMessage.className = 'os-dropdown-item';
                    helpMessage.textContent =
                        Handlebars.translation.prospect.not_enough_characters;
                    resultsContainerEl.innerHTML = helpMessage.outerHTML;
                    return showElem(resultsContainerEl);
                }
                
                quoteProspectTimeoutId = setTimeout(async () => {
                    const loadingEl = document.createElement('div');
                    loadingEl.style.cssText = `
                        display: flex;
                        align-items: center;
                        padding: 3px;
                    `;
                    addLoader(loadingEl, `${Handlebars.translation.general.loading}...`);
                    resultsContainerEl.innerHTML = loadingEl.outerHTML;

                    const {
                        data: prospects,
                        status,
                        message,
                    } = await this.app.service.searchProspects(e.target.value);

                    if (status !== 0) {
                        const helpMessage = document.createElement('p');
                        helpMessage.className = 'os-dropdown-item';
                        helpMessage.textContent = message;
                        resultsContainerEl.innerHTML =
                            helpMessage.outerHTML;
                    } else if (Array.isArray(prospects)) {
                        resultsContainerEl.innerHTML = '';

                        prospects.forEach((prospect) => {
                            const itemEl = document.createElement('a');
                            itemEl.className = 'os-dropdown-item';
                            itemEl.dataset.searchProspectId =
                                prospect.id;
                            itemEl.textContent = `${prospect.full_name} 
                                ${prospect.email ? `| ${prospect.email}` : ''} 
                                ${prospect.phone ? `| ${prospect.phone}`: ''} 
                                ${prospect.company_name ? `| ${prospect.company_name}` : ''} 
                                (${prospect.id})
                            `;
                            resultsContainerEl.append(itemEl);

                            const wUpdateProspect = async () => {
                                this.#updateProspect(prospect, false);
                                resultsContainerEl.innerHTML = '';
                                hideElem(resultsContainerEl);
                                
                                const {
                                    data: completeProspect,
                                    status,
                                    message,
                                } = await this.app.service.getProspect(
                                    prospect.id,
                                    true
                                );
                                
                                if (status === 0 && completeProspect) {
                                    this.data.quote.prospect = completeProspect;
                                    this.#updateLocalQuote();
                                    
                                    const modifyBtn = this.quoteEl.querySelector('#quote-modify-prospect');
                                    const loginBtn = this.quoteEl.querySelector('#quote-login-customer');
                                    
                                    if (modifyBtn && !this.data.read_only && !this.data.wildcard) {
                                        modifyBtn.classList.remove('os-quote-interact--disabled');
                                        modifyBtn.style.pointerEvents = '';
                                    }
                                    
                                    if (loginBtn && completeProspect.customer_id) {
                                        loginBtn.style.display = 'inline-block';
                                    }
                                }
                                
                                itemEl.removeEventListener(
                                    'click',
                                    wUpdateProspect
                                );
                            };

                            showElem(resultsContainerEl);
                            itemEl.addEventListener(
                                'click',
                                wUpdateProspect
                            );
                        });
                    }
                }, 1000);
                

                if (!listening) {
                    document.body.addEventListener(
                        'click',
                        handleOutsideDropdownClick
                    );
                    resultsContainerEl.classList.remove(
                        'os-input-dropdown--hidden'
                    );
                    listening = true;
                }
            } else if (e.target.id === 'product-input' || e.target.closest('#quote-share-form') || e.target.id === 'ai-additional-instruction') {
                // Nothing to do here
            } else {
                this.edited = true;
            }
        });

        this.quoteEl.addEventListener('click', async (e) => {

            if (e.target.id === 'quote-activities') {
                if (!this.data.quote.id) return;

                this.notif.reload({ type: 'loading' });
                this.notif.open();

                if (!this.data.read_only && this.edited) {
                    const quoteRes = await this.#saveQuote();
                    this.#updateLocalQuote();

                    if (quoteRes?.status !== 0) {
                        return this.notif.reload({
                            type: 'error',
                            message: quoteRes?.message,
                        });
                    }
                }

                const { data: activities } = await this.app.service.getActivities(
                    this.data.quote.id
                );

                if (activities) {
                    const activity = new Activity(
                        new Modal({
                            containerId: `activity-modal-${this.data.quote.id}`,
                            maxWidth: '700px',
                            height: '95%',
                            borderRadius: '0px',
                            padding: '0px',
                        }),
                        {
                            ...activities,
                            win: this.data.quote.win,
                            prospect: this.data.quote.prospect,
                            quote_number: this.data.quote.number,
                            quote_id: this.data.quote.id,
                            selected_impact: this.app.session.sessionDetails?.activity?.minimum_impact || 3
                        }, this.app
                    );
                    activity.open();
                    this.notif.close();
                } else {
                    this.notif.reload({ type: 'error' });
                }

                return;
            }

            if (e.target.id === 'product-submit') {
                const productInputEl = this.quoteEl.querySelector('#product-input');
                const productQuery = productInputEl.value;

                if (productQuery === '') return;

                this.notif.reload({ type: 'loading' });
                this.notif.open();

                const postProductSkuSubmit = (data) => {
                    if (data.redirect_url) {
                        window.location.href = getRedirectUrl(data.redirect_url);
                    }

                    if (data.quote) {
                        this.quoteEl = document.createElement('div');
                        this.data = { ...this.data, quote: data.quote, currency: data.currency, read_only: false, wildcard: false };
                        this.#focusSku = true;
                        this.load();
                        // Update LocalQuote to persist the new product data
                        this.#updateLocalQuote();
                    }

                    if (data.cart_loader_url) {
                        this.#onCloseRedirect = getRedirectUrl(data.cart_loader_url);
                    }
                }

                const handleNotif = () => {
                    this.notif.reload({
                        message: data.message,
                        type: 'warning',
                        onDefault: {
                            fn: (notification) => {
                                postProductSkuSubmit(data);
                                notification.close();
                            },
                            content: Handlebars.translation.general.accept,
                        },
                        onAlternative: {
                            fn: (notification) => {
                                productInputEl.value = '';
                                notification.close();
                            },
                            content: Handlebars.translation.general.cancel,
                        },
                    });
                }

                if (this.data.quote.seller_cart_id === LocalQuote.WILDCARD) {
                    const currencyCode = this.app.session.sessionDetails.currencyCode;
                    const {data, message, status} = await this.app.service.addSku(this.data.quote.seller_cart_id, productQuery, currencyCode);

                    if (data.message) {
                        return handleNotif();
                    } else {
                        postProductSkuSubmit(data);
                        return this.notif.close();
                    }
                }

                if (this.edited) {
                    const saveRes = await this.#saveQuote();
                    this.#updateLocalQuote();
    
                    if (saveRes?.status !== 0) {
                        return this.notif.reload({
                            type: 'error',
                            message: saveRes?.message,
                        });
                    }
                }

                const currencyCode = this.app.session.sessionDetails.currencyCode;
                const {data, status, message} = await this.app.service.addSku(this.data.quote.seller_cart_id, productQuery, currencyCode);

                if (status !== 0) {
                    return this.notif.reload({
                        type: 'error',
                        message: message,
                    });
                }

                if (data.message) {
                    handleNotif();
                } else {
                    postProductSkuSubmit(data);
                    this.notif.close();
                }
            }
            
            if (e.target.id === 'load-customer-cart') {
                const loadCustomerCart = new LoadCustomerCart(new Modal({ maxWidth: '500px', containerId: 'customer-cart-load' }), {
                    tcc: this.data.quote.tcc,
                    customerCartId: this.data.quote.customer_cart_id,
                    sellerCartId: this.data.quote.seller_cart_id,
                }, this.app);
                return loadCustomerCart.load();
            }

            if (['quote-new-prospect', 'quote-modify-prospect'].includes(e.target.id)) {
                e.preventDefault();
                const { storeId } = await this.app.service.getParamsForBCSuite();
                const isModify = !!e.target.id.includes('modify');

                const prospectData = isModify
                    ? { ...this.data.quote.prospect, store: storeId }
                    : { store: storeId };

                const updateProspectOnSuccess = (res) => {
                    if (res.data.prospect) {
                        this.#updateProspect(res.data.prospect);
                        this.edited = true;
                    }
                };

                const prospectForm = new ProspectForm(
                    new Modal({ maxWidth: '500px' }),
                    {
                        ...prospectData,
                        customer_groups: this.app.session.sessionDetails.customerGroups,
                        customer_create_optin: this.app.session.sessionDetails.prospect?.customer_create_optin || false,
                    },
                    this.app,
                    {
                        postSubmit: updateProspectOnSuccess,
                    }
                );

                return prospectForm.open();
            }

            if (e.target.id === 'quote-login-customer') {
                e.preventDefault();
                if (!this.data.quote.prospect?.customer_id) {
                    return;
                }
                const customerId = this.data.quote.prospect.customer_id;
                const { data, message } = await this.app.service.getCustomerLoginUrl(customerId);
                if (data?.url) {
                    window.location.href = getRedirectUrl(data.url);
                } else {
                    this.notif.reload({ type: 'error', message: message });
                    this.notif.open();
                }
            }

            if (e.target.id === 'preview-quote') {
                let quoteId = this.data.quote.id;

                this.notif.reload({ type: 'loading' });
                this.notif.open();
                if (!this.data.read_only) {

                    const quoteRes = await this.#saveQuote();

                    if (quoteRes?.status !== 0) {
                        return this.notif.reload({
                            type: 'error',
                            message: quoteRes?.message,
                        });
                    } 

                    // Quote ID is now updated in this.data.quote by #saveQuote
                    quoteId = this.data.quote.id;
                    this.#updateLocalQuote();
                }


                const { data, message } = await this.app.service.getPDFQuoteViewLink(quoteId);

                if (!data) {
                    return this.notif.reload({ type: 'error', message: message });
                }

                const iframe = `<iframe src="${data.link}" width="100%" style="height: 80vh;"></iframe>`;
                const pdfIframe = new Modal();
                pdfIframe.update(iframe);
                pdfIframe.open();

                return this.notif.close();
            }

            if (e.target.id === 'update-customer-cart') {
                if (!this.data.quote.customer_cart_id) {
                    return;
                }

                this.notif.reload({ type: 'loading' });
                this.notif.open();

                const quoteRes = await this.#saveQuote();
                this.#updateLocalQuote();

                if (quoteRes.status !== 0) {
                    this.notif.reload({
                        type: 'error',
                        message: quoteRes.message,
                    });
                    return;
                }
                const originalNode = e.target.cloneNode(true);
                let tcc = this.app.session.get().sessionDetails.tcc;
                e.target.innerHTML = `${Handlebars.translation.general.loading}...`;

                const updateRes = await this.app.service.updateCartWithTCC(
                    tcc,
                    this.data.quote.seller_cart_id
                );

                if (updateRes.status == 0) {
                    e.target.innerHTML = 'Cart updated';
                } else {
                    e.target.innerHTML =
                        'Failed to update cart: ' + updateRes.message;
                }

                setTimeout(() => {
                    e.target.replaceWith(originalNode);
                }, 1500);

                return this.notif.close();
            }

            if (e.target.id === 'ai-suggestion-trigger') {
                e.preventDefault();
                
                // Check if AI instructions are disabled
                if (e.target.hasAttribute('disabled')) {
                    return;
                }

                const aiInstructionInput = this.quoteEl.querySelector('#ai-additional-instruction');
                const additionalInstruction = aiInstructionInput?.value || '';
                
                // Disable the trigger to prevent multiple clicks
                e.target.setAttribute('disabled', 'true');
                
                // Get the current share medium
                const selectedMediumInput = this.quoteEl.querySelector('input[name="share_medium"]:checked');
                const isEmailMedium = selectedMediumInput?.value === 'email';
                
                // Store original values
                const subjectInput = this.quoteEl.querySelector('#quote-share-subject');
                const originalSubject = subjectInput ? subjectInput.value : '';
                const originalMessage = this.#messageEditor ? this.#messageEditor.getHTML() : '';
                
                // Get message editor element (the .ql-editor inside the rich text editor)
                const messageEditorEl = this.quoteEl.querySelector('#quote-share-message-editor .ql-editor');
                
                // Clear values and add shimmer class + "Thinking..." text simultaneously
                if (subjectInput) {
                    subjectInput.value = '';
                    if (isEmailMedium) {
                        subjectInput.classList.add('os-loading-shimmer');
                    }
                }
                
                if (messageEditorEl) {
                    messageEditorEl.classList.add('os-loading-shimmer');
                }

                // Show "Thinking..." with animated dots
                let dotCount = 0;
                const updateThinkingText = () => {
                    dotCount = (dotCount + 1) % 4;
                    const thinkingText = Handlebars.translation.quote.ai_thinking + '.'.repeat(dotCount);
                    
                    if (this.#messageEditor) {
                        this.#messageEditor.setHTML(thinkingText);
                    }
                    
                    if (isEmailMedium && subjectInput) {
                        subjectInput.value = thinkingText;
                    }
                };
                
                // Run immediately first time
                updateThinkingText();
                
                // Then start the interval
                const dotsInterval = setInterval(updateThinkingText, 500);

                try {
                    // Save quote if needed
                    let quoteId = this.data.quote.id;
                    
                    if (this.edited) {
                        const quoteRes = await this.#saveQuote();
                        
                        if (quoteRes.status !== 0) {
                            clearInterval(dotsInterval);
                            
                            // Remove shimmer classes
                            if (subjectInput) {
                                subjectInput.classList.remove('os-loading-shimmer');
                                subjectInput.value = originalSubject;
                            }
                            if (messageEditorEl) {
                                messageEditorEl.classList.remove('os-loading-shimmer');
                            }
                            
                            if (this.#messageEditor) {
                                this.#messageEditor.setHTML(originalMessage);
                            }
                            e.target.removeAttribute('disabled');
                            this.notif.reload({
                                type: 'error',
                                message: quoteRes.message,
                            });
                            this.notif.open();
                            setTimeout(() => this.notif.close(), 3000);
                            return;
                        }
                        
                        // Quote ID is now updated in this.data.quote by #saveQuote
                        quoteId = this.data.quote.id;
                        this.#updateLocalQuote();
                    }

                    const { data, status, message } = await this.app.service.getSuggestedMessage(
                        quoteId,
                        { additional_instruction: additionalInstruction }
                    );

                    clearInterval(dotsInterval);
                    e.target.removeAttribute('disabled');
                    
                    // Remove shimmer classes
                    if (subjectInput) {
                        subjectInput.classList.remove('os-loading-shimmer');
                    }
                    if (messageEditorEl) {
                        messageEditorEl.classList.remove('os-loading-shimmer');
                    }

                    if (status !== 0 || !data) {
                        // Restore original values
                        if (subjectInput) {
                            subjectInput.value = originalSubject;
                        }
                        if (this.#messageEditor) {
                            this.#messageEditor.setHTML(originalMessage);
                        }
                        this.notif.reload({
                            type: 'error',
                            message: message || 'Failed to get AI suggestion',
                        });
                        this.notif.open();
                        setTimeout(() => this.notif.close(), 3000);
                        return;
                    }

                    // Update subject if applicable and present in response. i.e email
                    if (data.subject) {
                        const subjectInput = this.quoteEl.querySelector('#quote-share-subject');
                        if (subjectInput) {
                            subjectInput.value = data.subject;
                            this.edited = true;
                        }
                    }

                    if (data.message) {
                        if (this.#messageEditor) {
                            await typewriterEffect(
                                data.message, 
                                (text) => this.#messageEditor.setHTML(text)
                            );
                        }
                        
                        const hiddenInput = this.quoteEl.querySelector('#quote-share-message-hidden');
                        if (hiddenInput) {
                            hiddenInput.value = data.message;
                        }
                        
                        this.edited = true;
                    } else {
                        // Restore original message if no new message from AI
                        if (this.#messageEditor) {
                            this.#messageEditor.setHTML(originalMessage);
                        }
                    }

                } catch (error) {
                    clearInterval(dotsInterval);
                    
                    // Remove shimmer classes
                    if (subjectInput) {
                        subjectInput.classList.remove('os-loading-shimmer');
                        subjectInput.value = originalSubject;
                    }
                    if (messageEditorEl) {
                        messageEditorEl.classList.remove('os-loading-shimmer');
                    }
                    
                    if (this.#messageEditor) {
                        this.#messageEditor.setHTML(originalMessage);
                    }
                    e.target.removeAttribute('disabled');
                    
                    this.notif.reload({
                        type: 'error',
                        message: 'An error occurred while getting AI suggestion',
                    });
                    this.notif.open();
                    setTimeout(() => this.notif.close(), 3000);
                    console.error('AI suggestion error:', error);
                }
                
                return;
            }
        });

        // Handle Enter key on AI instruction input
        const aiInstructionInput = this.quoteEl.querySelector('#ai-additional-instruction');
        if (aiInstructionInput) {
            aiInstructionInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const aiTrigger = this.quoteEl.querySelector('#ai-suggestion-trigger');
                    if (aiTrigger && !aiTrigger.hasAttribute('disabled')) {
                        aiTrigger.click();
                    }
                }
            });
        }
    };

    #enableShareOptions = () => {
        const shareEnd = this.quoteEl.querySelector('.os-quote-share__end');
        if (shareEnd) {
            shareEnd.removeAttribute('disabled');
        }
    }

    #enableAIInstructions = () => {
        const aiInstructionContainer = this.quoteEl.querySelector('.os-quote-details__ai-instruction');
        if (aiInstructionContainer) {
            aiInstructionContainer.removeAttribute('disabled');
            aiInstructionContainer.querySelectorAll('[disabled]').forEach((el) => {
                el.removeAttribute('disabled');
            });
        }
    }

    #getQuoteFormData = () => {
        let body = {
            id: this.data.quote.id,
            prospect: this.data.quote.prospect?.id,
            seller_cart_id: this.data.quote.seller_cart_id,
            discount_amount: this.roundPrice(this.data.totals.globalDiscount),
            tcc: this.app.session.get().sessionDetails.tcc,
            lines: [],
            custom_fields: {}
        };
        const formData = new FormData(
            this.quoteEl.querySelector('#quote-form')
        );

        let lines = [];
        let lineItem = {
            cart_line_id: null,
            discount_price: null,
            quote_product_name: null,
            quote_price: null,
            quote_discount_price: null,
            quantity: null
        };

        for (const [key, value] of formData.entries()) {
            if (key.startsWith('Line-')) {
                if (key.endsWith('-title')) {
                    lineItem.quote_product_name = value;
                    lineItem.cart_line_id = key
                        .substring(5)
                        .replace('-title', '')
                } else if (key.endsWith('-Discount')) {
                    lineItem.discount_price = value;
                    lineItem.quote_discount_price = value;
                    lineItem.cart_line_id = key
                        .substring(5)
                        .replace('-Discount', '')
                } else if (key.endsWith('-Qty')) {
                    lineItem.quantity = Number(value);
                    lineItem.cart_line_id = key
                        .substring(5)
                        .replace('-Qty', '')
                }
                if (!lineItem.quote_price) {
                    lineItem.quote_price = this.roundPrice(
                        this.#getLineItem(lineItem.cart_line_id).quote_price
                    );
                }
            } else if (key == 'prospect_id') {
                body[key] = this.data.quote.prospect?.id;
            } else if (key == 'prospect') {
                continue;
            } else if (key.startsWith('custom_field')) {
                let customKey = key.replace('custom_field_', '');
                if (body.custom_fields[customKey]) {
                    if (Array.isArray(body.custom_fields[customKey])) {
                        body.custom_fields[customKey].push(value);
                    } else {
                        body.custom_fields[customKey] = [body.custom_fields[customKey], value];
                    }
                } else {
                    body.custom_fields[customKey] = value;
                }
            } else {
                body[key] = value;
            }

            if (
                lineItem.cart_line_id !== null &&
                lineItem.discount_price !== null &&
                lineItem.quote_product_name !== null && 
                lineItem.quantity !== null
            ) {
                lines.push(lineItem);
                lineItem = {
                    cart_line_id: null,
                    discount_price: null,
                    quote_product_name: null,
                    quote_price: null,
                    quote_discount_price: null,
                    quantity: null
                };
            }
        }
        body.lines = lines;

        return body;
    };

    #saveQuote = async (e) => {
        let body = this.#getQuoteFormData();

        const res = await this.app.service.saveQuoteByCartId(
            this.data.quote.seller_cart_id,
            body
        );
        
        if (res.data) {
            this.edited = false;
            
            // Update this.data.quote with the latest data from the server
            if (res.data.id) {
                this.data.quote.id = res.data.id;
            }
            if (res.data.number) {
                this.data.quote.number = res.data.number;
            }
            if (res.data.state) {
                this.data.quote.state = res.data.state;
            }
            if (res.data.tcc) {
                this.data.quote.tcc = res.data.tcc;
            }
            
            this.app.session.sessionDetails = {
                ...this.app.session.sessionDetails,
                currentQuoteId: res.data.id,
                currentCartId: res.data.seller_cart_id,
                currentQuoteState: res.data.state,
                currentQuoteNumber: res.data.number,
                tcc: res.data.tcc,
            };
        }

        return res;
    };

    #getUpdatedQuoteData = () => {
        let quoteFormData = { ...this.data.quote, ...this.#getQuoteFormData() };
        const newLines = this.data.quote.lines.map((line) => {
            const qfd = quoteFormData.lines.find(
                (qf) => qf.cart_line_id === line.cart_line_id
            );
            return { ...line, ...qfd };
        });
        quoteFormData.lines = newLines;

        return {...quoteFormData, prospect: this.data.quote.prospect}
    }

    #updateLocalQuote = () => {
        const newFormData = this.#getUpdatedQuoteData();
        LocalQuote.set(this.data.quote.seller_cart_id, {...this.data, quote: newFormData});

        return newFormData;
    };

    #onClose = async () => {
        // Clean up rich text editor
        if (this.#messageEditor) {
            this.#messageEditor.destroy();
            this.#messageEditor = null;
        }
        
        this.notif.close();
        if (this.edited) {
          const saveAndStoreQuote = async () => {
            const { data } = await this.#saveQuote();
        
            if (!data) {
              console.error('Failed to save quote');
              throw new Error('Failed to save quote');
            }
        
            const updatedFormData = this.#getUpdatedQuoteData();
            const quoteData = {
              ...updatedFormData,
              id: data.id,
              number: data.number,
              tcc: data.tcc,
              customer_cart_id: data.customer_cart_id,
            };
        
            LocalQuote.set(this.data.quote.seller_cart_id, {...this.data, quote: quoteData});
        
            return {...this.data, quote: quoteData};
          };
        
          const promise = saveAndStoreQuote();
          LocalQuote.set(this.data.quote.seller_cart_id, promise);
        }


        if (this.#onCloseRedirect) {
            window.location.href = this.#onCloseRedirect;
        }
    };

    #updateDate = () => {
        const dateInput = this.quoteEl.querySelector('#quote-date');

        let date;

        if (this.data.quote.date) {
            date = this.data.quote.date;
        } else {
            const now = new Date();

            const day = ('0' + now.getDate()).slice(-2);
            const month = ('0' + (now.getMonth() + 1)).slice(-2);
            date = now.getFullYear() + '-' + month + '-' + day;
        }

        dateInput.value = date;
    };

    #getLineItem = (cartLineId) => {
        /* We assume line items id are unique */
        return this.data.quote.lines.find((item) => {
            return item.cart_line_id == cartLineId;
        });
    };

    #initCartLinePrices = () => {
        /* Update total price (with quote price) and total (base product price)  */
        this.data.quote.lines?.forEach((lineItem) => {
            const quoteLineEl = this.quoteEl.querySelector(
                `[data-quote-line-id="${lineItem.cart_line_id}"]`
            );
            quoteLineEl.querySelector('#quote-line-total').textContent =
                `${this.formatPrice(lineItem.quantity * lineItem.quote_price)}`;
            quoteLineEl.querySelector('#quote-line-regular-total').textContent =
                `${this.formatPrice(lineItem.quantity * lineItem.price)}`;
        });

        /* Instantiate our tempPrice variable for working with price without modifying the original product price */
        this.data.quote.lines?.forEach((lineItem) => {
            lineItem.tempPrice = lineItem.price;
            if (lineItem.quote_price > lineItem.price) {
                lineItem.tempPrice = lineItem.quote_price;
            }
        });
    };

    #initInputs = () => {
        let templateId;
        const savedTemplateId = this.data.quote.template;
        let optimalTemplateId, optimalTemplateName, optimalTemplateCount;

        if (this.data.templates.length && this.data.quote.lines?.length) {
            const optimalTemplate = this.#getTemplateForProductCount(
                this.data.templates,
                this.data.quote.lines?.length
            );

            optimalTemplateId = optimalTemplate.id;
            optimalTemplateName = optimalTemplate.name;
            optimalTemplateCount = optimalTemplate.product_count;
        }

        if (!savedTemplateId) {
            templateId = optimalTemplateId;
        } else {
            templateId = savedTemplateId;
        }

        if (templateId) {
            /* Recommend a different quote template to the user if appropiate */
            if (this.data.quote.cartUpdated) {
                const selectedTemplate = this.data.templates.find(
                    (template) => template.id == templateId
                );
                if (selectedTemplate.product_count !== optimalTemplateCount) {
                    this.notif.reload({
                        type: 'warning',
                        message: `${Handlebars.translation.quote.template_recommendation}: ${optimalTemplateName}.`,
                    });
                    this.notif.open();
                }
                this.data.quote.cartUpdated = false;
            }

            /* Auto-select the previously saved quote template */
            const templateEl = this.quoteEl.querySelector(`#template-${templateId}`);
            if (templateEl) {
                templateEl.checked = true;
            }
        }

        /* Auto-select the previously saved quote win */
        if (this.data.quote.win) {
            const winEl = this.quoteEl
                .querySelector('#quote-win')
                .querySelector(`[value="${this.data.quote.win}"]`);

            if (winEl) {
                winEl.selected = true;
            }
        }

        /* Catch enter keydown on product input */
        const productInputEl = this.quoteEl.querySelector('#product-input');

        if (productInputEl) {
            productInputEl.addEventListener('keydown', function (event) {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  document.getElementById('product-submit').click();
                }
            });

            if (this.#focusSku) {
                productInputEl.focus();
                this.#focusSku = false;
            }
        }

        /* Catch enter keydown on product submit */
        this.quoteEl.querySelector('#product-submit')?.addEventListener('keydown', function (event) {
            if (event.key === 'Enter') {
              event.preventDefault();
              document.getElementById('product-submit').click();
            }
        });

        if (this.data.quote.custom_fields) {
            for (const key in this.data.quote.custom_fields) {
              const values = this.data.quote.custom_fields[key];
              const valuesArray = Array.isArray(values) ? values : [values];
              const inputs = this.quoteEl.querySelectorAll(`input[name^="custom_field_${key}"]`);
              inputs.forEach(input => {
                if (valuesArray.includes(input.value)) {
                  input.checked = true;
                }
              });
            }
        }

        /* Initialize Rich Text Editor for message field */
        this.#initMessageEditor();
    };

    #initMessageEditor = () => {
        const editorContainer = this.quoteEl.querySelector('#quote-share-message-editor');
        const hiddenInput = this.quoteEl.querySelector('#quote-share-message-hidden');
        
        if (!editorContainer) return;

        const isDisabled = editorContainer.hasAttribute('data-disabled') || this.data.read_only || this.data.wildcard;
        
        if (this.#messageEditor) {
            this.#messageEditor.destroy();
        }

        this.#messageEditor = new RichTextEditor(editorContainer, {
            placeholder: Handlebars.translation.quote.message_placeholder,
            readOnly: isDisabled
        });

        // Set initial content if available
        if (this.data.quote.share_message) {
            this.#messageEditor.setHTML(this.data.quote.share_message);
        }

        // Update hidden input on content change
        this.#messageEditor.onChange(() => {
            const content = this.#messageEditor.getHTML();
            if (hiddenInput) {
                hiddenInput.value = content;
            }
            this.edited = true;
        });
    };

    #getDiscount = (discount) => {
        let value, kind;

        const isNumber = (n) => !isNaN(parseFloat(n)) && isFinite(n);
        const isPercentage = (n) => n.endsWith('%') && isNumber(n.slice(0, -1));
        const isSubtract = (n) => n.startsWith('-') && isNumber(n.substring(1));

        if (
            !discount ||
            (!isNumber(discount) &&
                !isSubtract(discount) &&
                !isPercentage(discount))
        ) {
            return { value: 0, kind: 'invalid' };
        }

        if (isSubtract(discount)) {
            value = parseFloat(discount.substring(1));
            kind = 'subtract';
        } else if (isPercentage(discount)) {
            value = discount.startsWith('-')
                ? parseFloat(discount.slice(1, -1))
                : parseFloat(discount.slice(0, -1));
            if (value > 100) {
                value = 0;
                kind = 'invalid';
            } else {
                kind = 'percentual';
            }
        } else {
            value = parseFloat(discount);
            kind = 'flat';
        }

        return { value, kind };
    };

    #updateProspect = (prospect, isComplete = true) => {
        this.data.quote = this.#updateLocalQuote();
        this.quoteEl = document.createElement('div');
        this.data.quote.prospect = prospect;
        
        this.load();
        this.edited = true;
        
        // Enable share options and AI instructions if both prospect and win are available
        if (prospect?.id && this.data.quote.win) {
            this.#enableShareOptions();
            this.#enableAIInstructions();
        }
        
        // If using incomplete data from search, disable buttons that need complete prospect
        if (!isComplete) {
            const modifyBtn = this.quoteEl.querySelector('#quote-modify-prospect');
            const loginBtn = this.quoteEl.querySelector('#quote-login-customer');
            
            if (modifyBtn) {
                modifyBtn.classList.add('os-quote-interact--disabled');
                modifyBtn.style.pointerEvents = 'none';
            }
            
            // Hide login button until we confirm customer_id exists
            if (loginBtn) {
                loginBtn.style.display = 'none';
            }
        }
    };

    #updateQuote = (quote) => {
        this.quoteEl = document.createElement('div');
        this.data.quote = quote;

        this.load();
    };

    #initShareForm = () => {
        const shareFormEl = this.quoteEl.querySelector('#quote-share-form');
        const share = this.app.session.sessionDetails.share || {};
        const shareMediums = this.app.session.sessionDetails.shareMediums;

        const openAppEl = shareFormEl.querySelector('#open-app');
        const openAppLabel = shareFormEl.querySelector('#open-app-label');
        const openAppCheckbox = shareFormEl.querySelector('input[name="open-app"]');

        function updateOpenApp(medium, ariaLabel) {
            if (shareMediums[medium]?.app) {
                openAppEl.style.visibility = 'visible';
                openAppLabel.textContent = Handlebars.applyTranslation(
                    'share.open_app',
                    ariaLabel
                );
            } else {
                openAppEl.style.visibility = 'hidden';
                if (openAppCheckbox) openAppCheckbox.checked = false;
            }
        }

        let selectedRadio = null;
        // Priority: quote.share_medium (quote-specific) over session share.medium (session default)
        const selectedMedium = this.data.quote.share_medium || share.medium;
        
        if (selectedMedium) {
            selectedRadio = this.quoteEl.querySelector(
                `input[name="share_medium"][value="${selectedMedium}"]`
            );
            if (selectedRadio) {
                selectedRadio.checked = true;
                updateOpenApp(selectedMedium, selectedRadio.ariaLabel);
                
                // Initialize subject field visibility
                const subjectContainer = this.quoteEl.querySelector('#quote-subject-container');
                if (selectedMedium === 'email') {
                    subjectContainer.style.display = 'block';
                } else {
                    subjectContainer.style.display = 'none';
                }
            }
        }

        if (openAppCheckbox) {
            openAppCheckbox.checked = !!share.app;
        }

        if (share.clipboard) {
            const clipboardRadio = shareFormEl.querySelector(
                `input[name="clipboard-copy"][value="${share.clipboard}"]`
            );
            if (clipboardRadio) clipboardRadio.checked = true;
        }

        const downloadCheckbox = shareFormEl.querySelector(
            'input[name="download-pdf"]'
        );
        if (downloadCheckbox) {
            downloadCheckbox.checked = !!share.download;
        }

        this.quoteEl.addEventListener('change', (e) => {
            if (e.target.name === 'share_medium') {
                updateOpenApp(e.target.value, e.target.ariaLabel);
                
                // Show/hide subject field based on share_medium selection
                const subjectContainer = this.quoteEl.querySelector('#quote-subject-container');
                const subjectLabel = this.quoteEl.querySelector('#quote-share-subject-label');
                const messageLabel = this.quoteEl.querySelector('#quote-share-message-label');
                
                if (e.target.value === 'email') {
                    subjectContainer.style.display = 'block';
                } else {
                    subjectContainer.style.display = 'none';
                }
                
                // Update subject label
                const subjectTranslation = getTranslationByPattern('share_medium_subject', e.target.value);
                if (subjectLabel && subjectTranslation) {
                    subjectLabel.textContent = subjectTranslation + ':';
                }
                
                // Update message label
                const messageTranslation = getTranslationByPattern('share_medium_message', e.target.value);
                if (messageLabel) {
                    if (messageTranslation) {
                        messageLabel.textContent = messageTranslation + ':';
                    } else {
                        messageLabel.textContent = Handlebars.translation.quote.message + ':';
                    }
                }
            }
        });

        shareFormEl.addEventListener('submit', async(e) => {
            e.preventDefault();

            let quoteId = this.data.quote.id;

            this.notif.reload({ type: 'loading' });
            this.notif.open();

            if (this.edited) {
                const {data} = await this.#saveQuote();
                quoteId = data.id;

                this.#updateQuote(data);
                this.#updateLocalQuote();
            }

            const shareFormData = new FormData(shareFormEl);
            const quoteFormData = new FormData(this.quoteEl.querySelector('#quote-form'));
            
            const { data, message } = await this.app.service.shareQuote(
                quoteId,
                {
                    medium: quoteFormData.get('share_medium'),
                    link_type: shareFormData.get('clipboard-copy'),
                    open_app: shareFormData.get('open-app'),
                    download: shareFormData.get('download-pdf'),
                }
            ); 

            this.app.session.sessionDetails = {
                ...this.app.session.sessionDetails,
                share: {
                    medium: quoteFormData.get('share_medium'),
                    app:  shareFormData.get('open-app'),
                    clipboard:  shareFormData.get('clipboard-copy'),
                    download:  shareFormData.get('download-pdf'),
                }
            };

            if (shareFormData.get('clipboard-copy') === 'copy-message') {
                try {
                    const messageHTML = this.#messageEditor ? this.#messageEditor.getHTML() : (quoteFormData.get('share_message') || '');
                    const messagePlainText = this.#messageEditor ? this.#messageEditor.getText() : (quoteFormData.get('share_message') || '').replace(/<[^>]*>/g, '');
                    
                    if (messageHTML.trim()) {
                        const clipboardItem = new ClipboardItem({
                            'text/html': new Blob([messageHTML], { type: 'text/html' }),
                            'text/plain': new Blob([messagePlainText], { type: 'text/plain' })
                        });
                        
                        await navigator.clipboard.write([clipboardItem]);
                        new Toast({
                            message: Handlebars.translation.general.message_copied,
                            type: 'info',
                            duration: 5000,
                        }).show();
                    } else {
                        new Toast({
                            message: Handlebars.translation.general.no_message_to_copy,
                            type: 'warning',
                            duration: 5000,
                        }).show();
                    }
                } catch (err) {
                    console.error('Failed to copy rich text message: ', err);
                    // Fallback to plain text if rich text copying fails
                    try {
                        const messagePlainText = this.#messageEditor ? this.#messageEditor.getText() : (quoteFormData.get('share_message') || '').replace(/<[^>]*>/g, '');
                        if (messagePlainText.trim()) {
                            await navigator.clipboard.writeText(messagePlainText);
                            new Toast({
                                message: Handlebars.translation.general.message_copied,
                                type: 'info',
                                duration: 5000,
                            }).show();
                        }
                    } catch (fallbackErr) {
                        console.error('Failed to copy message (fallback): ', fallbackErr);
                        this.notif.reload({
                            type: 'error',
                            message: Handlebars.translation.general.clipboard_copy_error,
                        });
                        return this.notif.open();
                    }
                }
            } else if (data.link_clipboard) {
                try {
                    await navigator.clipboard.writeText(data.link_clipboard);
                    
                    new Toast({
                        message: Handlebars.translation.general.link_copied,
                        type: 'info',
                        duration: 5000,
                    }).show();
                } catch (err) {
                    console.error('Failed to copy: ', err);
                    this.notif.reload({
                        type: 'error',
                        message: Handlebars.translation.general.clipboard_copy_error,
                    });
                    return this.notif.open();
                }
            }

            if (shareFormData.get('open-app') && data.link_redirect) {
                new Toast({
                    message: Handlebars.translation.share.app_will_open,
                    type: 'info',
                    duration: 5000,
                }).show();
                window.open(data.link_redirect, '_blank');
            }

            if (data.link_download) {
                try {
                    new Toast({
                        message: Handlebars.translation.quote.processing_quote_download,
                        type: 'info',
                        duration: 5000,
                    }).show();

                        const pdfLink = data.link_download;
                    const response = await fetch(pdfLink);

                    const contentType = response.headers.get("content-type") || "";
                    if (!response.ok || !contentType.includes("pdf")) {
                        throw new Error(`Invalid file type: ${contentType}`);
                    }

                    const contentDisposition = response.headers.get("content-disposition");
                    let filename = null;
                    
                    if (contentDisposition) {
                        // First try to get filename* (RFC 5987) which supports UTF-8 encoding
                        // Example: filename*=utf-8''Cotizaci%C3%B3n%20%20%2300264%20.pdf
                        const filenameStarMatch = contentDisposition.match(/filename\*=([^']*)'([^']*)'([^;\n]*)/);
                        if (filenameStarMatch && filenameStarMatch[3]) {
                            // Decode the UTF-8 encoded filename
                            filename = decodeURIComponent(filenameStarMatch[3]);
                        } else {
                            // Fall back to regular filename parameter
                            // Example: filename="Quote #00058 - Prospect Name.pdf"
                            const filenameMatch = contentDisposition.match(/filename[^*][^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                            if (filenameMatch && filenameMatch[1]) {
                                filename = filenameMatch[1].replace(/['"]/g, '');
                            }
                        }
                    }
                    
                    // If no filename from Content-Disposition, construct from quote data
                    if (!filename) {
                        const quoteTranslation = Handlebars.translation.general.quote;
                        const quoteNumber = this.data.quote.number || quoteId;
                        const prospectName = this.data.quote.prospect?.full_name || '';
                        
                        if (prospectName) {
                            filename = `${quoteTranslation} #${quoteNumber} - ${prospectName}.pdf`;
                        } else {
                            filename = `${quoteTranslation} #${quoteNumber}.pdf`;
                        }
                    }

                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);

                    const a = document.createElement("a");
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);

                    URL.revokeObjectURL(url);

                } catch (err) {
                    console.error('Failed to download PDF: ', err);
                    new Toast({
                        message: Handlebars.translation.quote.error_processing_quote_download,
                        type: 'error',
                        duration: 5000,
                    }).show();
                }
            }

            try {
                new Toast({
                    message: Handlebars.translation.menu.preparing_new_cart + '...',
                    type: 'info',
                    duration: 5000,
                }).show();
                
                await prepareNewCart(this.app.service, this.app.session);
                
                window.location.href = '/';
            } catch (err) {
                console.error('Failed to prepare new cart: ', err);
            }

            this.notif.close();
            this.close();
        });
    }

    #getTemplateForProductCount = (templates, count) => {
        const validTemplates = templates?.filter(
            (template) => template.product_count >= count
        );

        if (validTemplates.length === 0) {
            return templates[0];
        }

        return validTemplates.reduce((best, template) =>
            template.product_count < best.product_count ? template : best
        );
    };

    #updateTotals = (arg) => {
        const totals = this.data.quote.lines.reduce(
            (prev, curr) => {
                return {
                    initialTotal:
                        prev.initialTotal + curr.quantity * curr.tempPrice,
                    subtotal: prev.subtotal + curr.quantity * curr.quote_price,
                    globalDiscount: prev.globalDiscount,
                };
            },
            { initialTotal: 0, subtotal: 0, globalDiscount: 0 }
        );
        const lineDiscounts = totals.initialTotal - totals.subtotal;
        const discount = this.#getDiscount(this.data.quote.discount);
        let grandTotal = totals.subtotal;

        if (discount.kind === 'invalid') {
            grandTotal = totals.subtotal;
            this.quoteEl.querySelector('#quote-global-discount-value').value =
                '';
        } else if (discount.kind === 'percentual') {
            if (discount.value >= 0 && discount.value <= 100) {
                totals.globalDiscount = grandTotal * (discount.value / 100);
                grandTotal = grandTotal - totals.globalDiscount;
            } else {
                grandTotal = totals.subtotal;
                totals.globalDiscount = 0;
            }
        } else if (discount.kind === 'subtract') {
            if (discount.value < totals.subtotal) {
                totals.globalDiscount = discount.value;
                grandTotal -= totals.globalDiscount;
            } else {
                grandTotal = 0;
                totals.globalDiscount = totals.subtotal;
            }
        } else {
            this.quoteEl.querySelector('#quote-global-discount-value').value =
                '';
        }

        this.quoteEl.querySelector('#quote-initial-total').textContent =
            `${this.formatPrice(totals.initialTotal)}`;
        this.quoteEl.querySelector('#quote-sub-total').textContent =
            `${this.formatPrice(totals.subtotal)}`;
        this.quoteEl.querySelector('#quote-discount-total').textContent =
            `${this.formatPrice(lineDiscounts)}`;
        this.quoteEl.querySelector('#quote-grand-total').textContent =
            `${this.formatPrice(grandTotal)}`;

        this.data.totals = {
            ...totals,
            grandTotal,
            discount,
        };

        if (arg) {
            flashElement(this.quoteEl.querySelector('#quote-initial-total'));
            flashElement(this.quoteEl.querySelector('#quote-sub-total'));
            flashElement(this.quoteEl.querySelector('#quote-discount-total'));
            flashElement(this.quoteEl.querySelector('#quote-grand-total'));
        } else {
            this.quoteEl.querySelector('#quote-initial-total').textContent =
                `${this.formatPrice(totals.initialTotal)}`;
        }
    };

    #intervalSave = () => {
        const saveEl = this.quoteEl.querySelector('#quote-save-status');
        const baseClass = saveEl.className; 
        const saveTxt = saveEl.querySelector('#save-text');

        const classStates = {
            saved: `${baseClass} ${baseClass}--saved`,
            error: `${baseClass} ${baseClass}--error`,
            saving: `${baseClass} ${baseClass}--saved`,
        };

        if (this.saving.interval) {
            clearInterval(this.saving.interval);
            this.saving.retries = 0;
        }

        this.saving.interval = setInterval(async () => {
            if (this.edited && this.saving.retries <= 3) {
                saveEl.className = classStates.saving;

                const {status, message} = await this.#saveQuote();

                if (status === 0) {
                    saveEl.className = classStates.saved;
                    this.saving.retries = 0;
                } else {
                    this.saving.retries++;
                    saveEl.className = classStates.error;
                    if (message) {
                        saveTxt.textContent = message;
                    }
                }

                setTimeout(() => {
                    saveEl.className = baseClass;
                    saveTxt.textContent = '';
                }, 3000);
            }
        }, 30 * 1000);
    }

    load = () => {
        if (!LocalQuote.has(this.data.quote.seller_cart_id)) {
            LocalQuote.set(this.data.quote.seller_cart_id, this.data);
        }

        /* Override modal method to save this quote whenever is closed */
        this.view.onClose = this.data.read_only || this.data.wildcard ? null : this.#onClose;
        this.quoteEl.innerHTML = quoteFormHndb(this.data);
        this.quoteEl.classList.add('os-quote-container');

        /* Tooltip removed since load customer cart functionality moved to quote list */

        /* Get stuff ready */
        this.#initCartLinePrices();
        this.#initInputs();
        this.#intervalSave();
        this.#initShareForm();

        if (this.quoteEl.querySelector('[data-cart-totals]')) {
            this.events.on('update:cartline', this.#updateTotals);
            this.#updateTotals();
        }

        /* Load content into modal */
        this.view.update(this.quoteEl);
        this.#updateDate();
        this.#listeners();
    };
}

export default Quote;
